import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/format'

export interface StatTileProps {
  label: ReactNode
  /** The number. Pass a formatted string or a node; null shows a dash. */
  value: ReactNode | null
  hint?: ReactNode
  /** Makes the tile a link (e.g. to a filtered list). */
  to?: string
  tone?: 'default' | 'accent' | 'warn' | 'bad'
  className?: string
}

const VALUE_TONE = { default: 'text-ink', accent: 'text-accent', warn: 'text-warn', bad: 'text-bad' }

/** KPI tile: small label, big condensed number, optional hint line. */
export function StatTile({ label, value, hint, to, tone = 'default', className }: StatTileProps) {
  const body = (
    <>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className={cn('mt-1 font-display text-[2rem] font-extrabold leading-none tabular', VALUE_TONE[tone])}>{value ?? '–'}</p>
      {hint ? <p className="mt-1.5 text-2xs text-subtle">{hint}</p> : null}
    </>
  )
  const base = 'block min-w-0 rounded-lg border border-line bg-panel px-4 py-3'
  return to ? (
    <Link to={to} className={cn(base, 'transition-colors hover:border-line-strong', className)}>
      {body}
    </Link>
  ) : (
    <div className={cn(base, className)}>{body}</div>
  )
}
